import { createContext, useState, useEffect } from "react";

export const CartContext = createContext(null);

// Initialize directly from localStorage — no useEffect flash
function loadCart() {
    try {
        const saved = localStorage.getItem("cart");
        return saved ? JSON.parse(saved) : [];
    } catch {
        return [];
    }
}

export function CartProvider({ children }) {
    const [cartItems, setCartItems] = useState(loadCart);

    // Sync to localStorage on every change
    useEffect(() => {
        localStorage.setItem("cart", JSON.stringify(cartItems));
    }, [cartItems]);

    const addToCart = (product, quantity = 1, variation = null) => {
        setCartItems((prev) => {
            const key = variation
                ? `${product.id}-${variation.id}`
                : `${product.id}`;
            const existing = prev.find((item) => item.key === key);
            if (existing) {
                return prev.map((item) =>
                    item.key === key
                        ? { ...item, quantity: item.quantity + quantity }
                        : item,
                );
            }
            return [
                ...prev,
                {
                    key,
                    product,
                    variation,
                    quantity,
                    price: variation ? variation.price : product.price,
                },
            ];
        });
    };

    const removeFromCart = (key) => {
        setCartItems((prev) => prev.filter((item) => item.key !== key));
    };

    const updateQuantity = (key, quantity) => {
        if (quantity <= 0) return removeFromCart(key);
        setCartItems((prev) =>
            prev.map((item) =>
                item.key === key ? { ...item, quantity } : item,
            ),
        );
    };

    const clearCart = () => setCartItems([]);

    const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    const cartTotal = cartItems.reduce(
        (sum, item) => sum + parseFloat(item.price || 0) * item.quantity,
        0,
    );

    return (
        <CartContext.Provider
            value={{
                cartItems,
                addToCart,
                removeFromCart,
                updateQuantity,
                clearCart,
                cartCount,
                cartTotal,
            }}
        >
            {children}
        </CartContext.Provider>
    );
}
